/* eslint-disable max-lines */
import type { IndexedDBConnector } from '@/types/accessor/common/connector'
import type { ObjectStoreName, SendState } from '@/types/accessor/indexedDB'
import { METHOD, SEND_STATE, UPLOADED_SEND_STATE } from '@/constants/accessor/indexedDB/indexedDB'
import * as indexedDB from './common/indexedDBController'
import { handleError } from '../util/errorHandler'
import type { StoreValue } from 'idb'
import type { IndexName } from './common/indexedDBController'
import type {
  ResultSummary,
  ResGetItemInspectionResult,
  ResQueryInspectionResult,
  ArgUpsertItemInspectionResult,
  ArgQueryInspectionResult,
  IDBInspectionResult,
  IDBContainer
} from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'
import { cloneDeep } from 'lodash'

export const STORE_NAME: ObjectStoreName = 'inspection_result'
const IDB_ERROR_CODE = 'idb_03'

/**
 * 更新用パラメータ
 */
export interface UpdateSendStatesParam {
  data: IDBInspectionResult
  sendState: SendState
}

/**
 * インデックスでの検索用パラメータ
 */
export interface GetAllFromIndexParam {
  indexName: IndexName
  indexState: string
}

/**
 * IndexedDBにデータを挿入する
 * @param data 挿入するデータ
 * @returns なし
 */
export const set: IndexedDBConnector<IDBInspectionResult, undefined, void> = async (
  data?: IDBInspectionResult
): Promise<void> => {
  if (!data) {
    console.error('Data is undefined')
    return
  }
  await indexedDB
    .set(STORE_NAME, data, METHOD.GET, SEND_STATE.FETCHED, data.assessmentId)
    .catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * IndexedDBに全てのデータを挿入する
 * @param data 挿入するデータ
 * @returns なし
 */
export const setAll: IndexedDBConnector<ResQueryInspectionResult, undefined, void> = async (
  data?: ResQueryInspectionResult
): Promise<void> => {
  if (!data) {
    console.error('Data is undefined')
    return
  }
  await Promise.all(
    data.resultList
      .map(async (d: ResultSummary) => {
        await indexedDB
          .set(STORE_NAME, d, METHOD.GET, SEND_STATE.FETCHED, d.assessmentId)
          .catch(async err => {
            throw err
          })
      })
      .flat()
  ).catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * inspectionResultテーブルにデータを設定する
 * すでにデータがある場合、DynamoDBにアップロードされたデータのみ上書きする
 * needRecords=trueの場合はupsertする ※バックエンドで生成されるデータはないのでR5.0では未使用
 * @param data inspectionResultのデータ | needRecords=trueの場合は調査実施結果API(query)レスポンス
 * @returns なし
 */
export const setOnlyUpdated: IndexedDBConnector<
  ArgUpsertItemInspectionResult | ResQueryInspectionResult,
  undefined,
  void
> = async (data?: ArgUpsertItemInspectionResult | ResQueryInspectionResult): Promise<void> => {
  if (!data) {
    return
  }
  if ('resultList' in data) {
    // needRecords=trueの場合のdata
    for (const item of data.resultList) {
      const idbData: IDBContainer = await indexedDB
        .get<StoreValue<string, string>>(STORE_NAME, item.assessmentId)
        .catch(async () => {
          await handleError(IDB_ERROR_CODE)
        })
      if (
        idbData == undefined ||
        (idbData != undefined &&
          idbData.data?.assessmentId === item.assessmentId &&
          !UPLOADED_SEND_STATE.includes(idbData.send_state as SendState))
      ) {
        const idbInsertReq: IDBInspectionResult = {
          ...item,
          govCode: data.govCode,
          disasterId: data.disasterId,
          insertId: item.insertId,
          insertDate: item.insertDate,
          updateId: item.updateId,
          updateDate: item.updateDate
        }
        await indexedDB.set(
          STORE_NAME,
          idbInsertReq,
          METHOD.GET,
          SEND_STATE.FETCHED,
          item.assessmentId
        )
        continue
      }
      await set({
        ...idbData.data,
        building: {
          ...idbData.data.building,
          buildingId: item.building.buildingId
        }
      })
    }
  } else {
    // needRecords=falseの場合のdata
    for (const item of data.inspectionResultList) {
      const idbData: IDBContainer = await indexedDB
        .get<StoreValue<string, string>>(STORE_NAME, item.assessmentId)
        .catch(async () => {
          await handleError(IDB_ERROR_CODE)
        })
      if (idbData === undefined) continue
      if (
        idbData != undefined &&
        idbData.data?.assessmentId === item.assessmentId &&
        !UPLOADED_SEND_STATE.includes(idbData.send_state as SendState)
      ) {
        continue
      }
      await set({
        ...idbData.data,
        building: {
          ...idbData.data.building,
          buildingId: item.building.buildingId
        }
      })
    }
  }
}

/**
 * IndexedDBのデータを更新する
 * @param data 更新データ
 * @returns 更新結果
 */
export const update: IndexedDBConnector<ArgUpsertItemInspectionResult, undefined, unknown> = async (
  data?: ArgUpsertItemInspectionResult
): Promise<unknown> => {
  if (!data) {
    console.error('Data is undefined')
    return
  }
  try {
    const today = new Date()
    for (const item of data.inspectionResultList) {
      const reqData: IDBInspectionResult = cloneDeep({
        ...item,
        govCode: data.govCode,
        disasterId: data.disasterId,
        insertId: item.userId,
        insertDate: today.toString(),
        updateId: item.userId,
        updateDate: today.toString()
      })
      await indexedDB.set(STORE_NAME, reqData, METHOD.UPDATE, SEND_STATE.EDITED, item.assessmentId)
    }
  } catch (err) {
    await handleError(IDB_ERROR_CODE)
    return {
      result: `IndexDBのレコードの更新に失敗しました。\nerror: ${err}`
    }
  }
  return {
    result: 'IndexDBのレコードの更新に成功しました。'
  }
}

/**
 * 指定されたキー情報に基づくデータをIndexedDBから取得する
 * @param params データ取得用のキー情報
 * @returns 取得したデータ
 */
export const get: IndexedDBConnector<
  Pick<ResGetItemInspectionResult, 'assessmentId'>,
  undefined,
  unknown
> = async (param?: Pick<ResGetItemInspectionResult, 'assessmentId'>): Promise<unknown> => {
  if (!param) {
    console.error('Data is undefined')
    return
  }
  const res = await indexedDB
    .get<StoreValue<string, string>>(STORE_NAME, param.assessmentId)
    .catch(async () => await handleError(IDB_ERROR_CODE))
  if (res !== undefined) return res.data
  return undefined
}

/**
 * inspectionResultテーブルのすべてのデータを返却する
 * @param なし
 * @returns 取得結果
 */
export const getAll: IndexedDBConnector<
  undefined,
  undefined,
  unknown
> = async (): Promise<unknown> => {
  return await indexedDB.getAll(STORE_NAME).catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * 指定したインデックスの値に対応するデータをすべて取得する
 * @param param インデックス情報
 * @returns 取得結果
 */
export const getAllFromIndex: IndexedDBConnector<
  GetAllFromIndexParam,
  undefined,
  IDBContainer[]
> = async (param?: GetAllFromIndexParam): Promise<IDBContainer[] | undefined> => {
  if (!param) {
    console.error('Data is undefined')
    return
  }
  return await indexedDB
    .getAllFromIndex(STORE_NAME, param.indexName, param.indexState)
    .catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * データを削除する
 * @param data 削除対象データ
 * @returns なし
 */
export const deleteByKey: IndexedDBConnector<
  ResGetItemInspectionResult,
  undefined,
  unknown
> = async (data?: ResGetItemInspectionResult): Promise<unknown> => {
  if (!data) {
    console.error('Data is undefined')
    return
  }
  await indexedDB
    .deleteByKey(STORE_NAME, data.assessmentId)
    .catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * 指定した検索条件に適合するデータを取得する。
 * @param param 検索条件
 * @returns 適合したデータ。該当データが存在しない場合、不正なデータ形式を検出した場合、
 *          またはエラー発生時には `undefined` を返す。
 */
export const query: IndexedDBConnector<
  ArgQueryInspectionResult,
  undefined,
  ResQueryInspectionResult | undefined
> = async (param?: ArgQueryInspectionResult): Promise<ResQueryInspectionResult | undefined> => {
  try {
    const all = (await indexedDB.getAll(STORE_NAME)) as IDBContainer[]
    if (!Array.isArray(all)) {
      return undefined
    }

    const { disasterId, govCode, buildingIdList, quadKey, insertId, targetDate } = param ?? {}

    const normalizedTargetDate = targetDate?.trim()
    const normalizedQuadKey = quadKey?.trim()

    /**
     * 災害ID(disasterId)、自治体コード(govCode)、作成者(insertId)、quadKey、調査日
     * のうち、指定した条件にすべて適合するかを判定する（指定していない条件は無視する）。
     */
    const matchesFilters = (record: IDBInspectionResult): boolean => {
      if (disasterId && record.disasterId !== disasterId) return false
      if (govCode && record.govCode !== govCode) return false

      if (insertId && record.insertId !== insertId) return false

      if (normalizedTargetDate) {
        const inspectedDate = record.assessmentInfo.inspectedDatetime
        // ISO 8601形式の日時(YYYY-MM-DDTHH:MM:SS+HH:MM)からTの前の日付部分（YYYY-MM-DD）を抽出
        const inspectedDateStr = inspectedDate ? inspectedDate.split('T')[0] : undefined
        if (inspectedDateStr !== normalizedTargetDate) return false
      }

      if (normalizedQuadKey) {
        const recordQuadKey = record.building.quadkey
        if (!recordQuadKey || recordQuadKey !== normalizedQuadKey) return false
      }

      if (Array.isArray(buildingIdList) && buildingIdList.length > 0) {
        const id = record.building.buildingId
        if (!id || !buildingIdList.includes(id)) return false
      }

      return true
    }

    const resultList = all
      .map(entry => entry?.data)
      .filter((record): record is IDBInspectionResult => record != null)
      .filter(matchesFilters)
      .map(record => cloneDeep(record))

    return {
      govCode: govCode ?? resultList[0]?.govCode ?? '',
      disasterId: disasterId ?? resultList[0]?.disasterId ?? '',
      resultList
    }
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'Unknown error'
    await handleError(IDB_ERROR_CODE, `query処理エラー: ${errorMessage}`)
    return undefined
  }
}

/**
 * indexedDBに存在する中断調査データのsendStateを引数のステータスに更新する。
 * @param data sendStateがeditedのindexedDBに存在する中断調査データ
 * @param sendStates 更新したいステータス値。'fetched' | 'edited' | 'uploadSuccess' | 'uploadFailed'
 * @returns なし
 */
export const updateSendStates: IndexedDBConnector<
  UpdateSendStatesParam,
  undefined,
  unknown
> = async (param?: UpdateSendStatesParam): Promise<unknown> => {
  if (!param) {
    console.error('Data is undefined')
    return
  }
  return await indexedDB
    .set(STORE_NAME, param.data, METHOD.UPDATE, param.sendState, param.data.assessmentId)
    .catch(async () => await handleError(IDB_ERROR_CODE))
}

/**
 * IndexedDB(inspection_result)から有効期限切れのデータを一括削除する
 * @param なし
 * @returns なし
 */
export const bulkDeleteExpiredItemsFromIDB: IndexedDBConnector<
  undefined,
  undefined,
  unknown
> = async (): Promise<unknown> => {
  return await indexedDB
    .bulkDeleteExpiredItemsFromIDB(STORE_NAME)
    .catch(async () => await handleError(IDB_ERROR_CODE))
}
